/**
 * Mac Studio Layout Component
 *
 * Main layout wrapper with dynamic wallpaper, grid system,
 * menu bar, window management, and responsive behavior.
 */

import { ReactNode, useEffect, useState } from 'react'
import { MacStudioMenuBar } from './MacStudioMenuBar'
import { WallpaperBackground } from './WallpaperBackground'
import { WindowManagerProvider } from '@/contexts/WindowManagerContext'
import { WallpaperProvider } from '@/contexts/WallpaperContext'
import { useIsMobile } from '@/hooks/useIsMobile'

export interface MacStudioLayoutProps {
  sidebar?: ReactNode
  children: ReactNode
  widgets?: ReactNode
  className?: string
  /** Hide menu bar (useful for embedded contexts) */
  hideMenuBar?: boolean
  /** Enable draggable/resizable windows (desktop only) */
  enableWindowManagement?: boolean
}

export function MacStudioLayout({
  sidebar,
  children,
  widgets,
  className = '',
  hideMenuBar = false,
  enableWindowManagement = true,
}: MacStudioLayoutProps) {
  const [scrollHue, setScrollHue] = useState(0)
  const isMobile = useIsMobile()

  // Update background hue based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight)
      const hue = Math.min(scrollPercent * 60, 60) // Max 60 degree hue shift
      setScrollHue(hue)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const desktopClasses = [
    'mac-studio-desktop',
    !hideMenuBar && 'mac-studio-desktop--with-menubar',
    enableWindowManagement && !isMobile && 'mac-studio-desktop--managed',
    className,
  ].filter(Boolean).join(' ')

  const content = (
    <>
      {/* macOS-style Menu Bar */}
      {!hideMenuBar && <MacStudioMenuBar />}

      <WallpaperBackground scrollHue={scrollHue}>
        <div
          className={desktopClasses}
          data-layout-theme="mac-studio"
        >
          {/* Desktop with managed windows */}
          {enableWindowManagement && !isMobile ? (
            <div className="mac-studio-desktop-area">
              {/* Sidebar - not managed, stays in flow */}
              {sidebar && (
                <div className="mac-studio-sidebar-container">
                  {sidebar}
                </div>
              )}
              {/* Windows render absolutely positioned */}
              {children}
              {widgets}
            </div>
          ) : (
            /* Mobile/non-managed layout - traditional grid */
            <div className="mac-studio-grid">
              {/* Sidebar */}
              {sidebar && sidebar}

              {/* Main Content Area */}
              <div className="mac-studio-main">
                {/* Main Content Window */}
                {children}

                {/* Widgets Row */}
                {widgets && (
                  <div className="mac-studio-widgets">
                    {widgets}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </WallpaperBackground>
    </>
  )

  // Wrap with providers
  const wrappedContent = enableWindowManagement ? (
    <WindowManagerProvider disabled={isMobile}>
      {content}
    </WindowManagerProvider>
  ) : content

  // Always wrap with WallpaperProvider for shared state
  return (
    <WallpaperProvider>
      {wrappedContent}
    </WallpaperProvider>
  )
}
